import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import moment from "moment";
import {
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Typography,
} from "antd";
import { DownloadOutlined, EnvironmentOutlined, ReloadOutlined } from "@ant-design/icons";
import "leaflet/dist/leaflet.css";
import { AppStateContext } from "../../context/AppContext";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import * as XLSX from "xlsx";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const defaultCenter = [28.6139, 77.209];
const mapHeight = 560;
const locationWindowOptions = [
  { label: "Last 15 minutes", value: 0.25 },
  { label: "Last 30 minutes", value: 0.5 },
  { label: "Last 45 minutes", value: 0.75 },
  ...Array.from({ length: 24 }, (_, index) => {
    const hours = index + 1;
    return { label: `Last ${hours} hour${hours === 1 ? "" : "s"}`, value: hours };
  }),
];

function formatWindowLabel(windowHours, capitalize = false) {
  const prefix = capitalize ? "Last" : "last";
  if (windowHours < 1) {
    return `${prefix} ${Math.round(windowHours * 60)} minutes`;
  }
  return `${prefix} ${windowHours} hour${windowHours === 1 ? "" : "s"}`;
}

function hasValidCoordinates(location) {
  const latitude = Number(location?.lastLocationLatitude);
  const longitude = Number(location?.lastLocationLongitude);
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
}

function MapFitBounds({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (!locations.length) return;
    const points = locations.map((location) => [
      Number(location.lastLocationLatitude),
      Number(location.lastLocationLongitude),
    ]);
    if (points.length === 1) {
      map.flyTo(points[0], 14);
    } else {
      map.flyToBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 14 });
    }
  }, [locations, map]);

  return null;
}

function formatLastSeen(value) {
  return value && moment(value).isValid()
    ? moment(value).format("DD MMM YYYY, hh:mm A")
    : "Not available";
}

export default function LocationDashboard() {
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [summary, setSummary] = useState({ totalUsers: 0 });
  const [locationWindowHours, setLocationWindowHours] = useState(24);
  const [activityLoading, setActivityLoading] = useState(false);
  const [inactiveUsersVisible, setInactiveUsersVisible] = useState(false);
  const [inactiveUserList, setInactiveUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  setPageHeading("Location Dashboard");

  const fetchLocations = () => {
    setLoading(true);
    request({
      url: apiPath.locationDashboard,
      method: "GET",
      onSuccess: (response) => {
        const data = response?.data || {};
        setLocations(Array.isArray(data.locations) ? data.locations : []);
        setSummary({
          totalUsers: Number(data.totalUsers) || 0,
        });
        setError(null);
        setLoading(false);
      },
      onError: (err) => {
        setError(err?.message || "Failed to load location dashboard");
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    setActivityLoading(true);
    const query = new URLSearchParams();
    query.append("windowHours", String(locationWindowHours));

    request({
      url: `${apiPath.userManagementActivitySummary}?${query.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setInactiveUserList(data?.inactiveUserList || []);
        setActivityLoading(false);
      },
      onError: (err) => {
        setInactiveUserList([]);
        setActivityLoading(false);
        ShowToast(err?.message || "Failed to fetch inactive users", Severty.ERROR);
      },
    });
  }, [locationWindowHours]);

  const filteredLocations = useMemo(() => {
    const threshold = Date.now() - locationWindowHours * 60 * 60 * 1000;

    return locations.filter((location) => {
      const lastLocationAt = moment(location.lastLocationAt).valueOf();
      return Number.isFinite(lastLocationAt) && lastLocationAt >= threshold;
    });
  }, [locations, locationWindowHours]);

  const mapLocations = useMemo(
    () => filteredLocations.filter(hasValidCoordinates),
    [filteredLocations]
  );
  const mapCenter = mapLocations.length
    ? [
        Number(mapLocations[0].lastLocationLatitude),
        Number(mapLocations[0].lastLocationLongitude),
      ]
    : defaultCenter;
  const usersWithLocation = mapLocations.length;
  const usersWithoutLocation = Math.max(summary.totalUsers - usersWithLocation, 0);

  const downloadInactiveUsersExcel = () => {
    if (!inactiveUserList.length) {
      ShowToast("No inactive users to download", Severty.INFO);
      return;
    }

    const rows = inactiveUserList.map((user, index) => ({
      "#": index + 1,
      Name: user.name && user.name !== "—" ? user.name : "",
      "Mobile Number": user.mobileNumber && user.mobileNumber !== "—" ? user.mobileNumber : "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inactive users");
    XLSX.writeFile(
      workbook,
      `inactive-users-${formatWindowLabel(locationWindowHours).replace("last ", "")}-${new Date().toISOString().slice(0, 10)}.xlsx`
    );
    ShowToast("Excel file downloaded", Severty.SUCCESS);
  };

  if (loading) {
    return (
      <div style={{ minHeight: 320, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Spin size="large" tip="Loading locations…" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <Typography.Text type="danger">{error}</Typography.Text>
        <div style={{ marginTop: 12 }}>
          <Button type="primary" icon={<ReloadOutlined />} onClick={fetchLocations}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Users in my scope" value={summary.totalUsers} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Users with latest location" value={usersWithLocation} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Location not available" value={usersWithoutLocation} /></Card>
        </Col>
      </Row>

      <Card
        title={<span><EnvironmentOutlined /> Latest user locations</span>}
        extra={(
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Select
              value={locationWindowHours}
              options={locationWindowOptions}
              onChange={setLocationWindowHours}
              style={{ minWidth: 130 }}
            />
            <Button
              type="primary"
              ghost
              loading={activityLoading}
              onClick={() => setInactiveUsersVisible(true)}
            >
              List of Inactive Users
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchLocations}>
              Refresh
            </Button>
          </div>
        )}
        style={{ marginTop: 16 }}
      >
        <div style={{ height: mapHeight, borderRadius: 8, overflow: "hidden" }}>
          <MapContainer
            center={mapCenter}
            zoom={mapLocations.length ? 6 : 5}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapFitBounds locations={mapLocations} />
            {mapLocations.map((location) => {
              const position = [
                Number(location.lastLocationLatitude),
                Number(location.lastLocationLongitude),
              ];
              return (
                <Marker key={String(location._id)} position={position}>
                  <Tooltip permanent direction="top" offset={[0, -24]}>
                    {location.name || location.mobileNumber || "User"}
                  </Tooltip>
                  <Popup>
                    <div style={{ minWidth: 210 }}>
                      <strong>{location.name || "Unnamed user"}</strong>
                      <div>{location.mobileNumber || "Mobile not available"}</div>
                      <div style={{ marginTop: 6 }}>{location.lastLocation || "Address not available"}</div>
                      <div style={{ color: "#8c8c8c", marginTop: 4 }}>
                        Last seen: {formatLastSeen(location.lastLocationAt)}
                      </div>
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: "4px 0 0" }}
                        onClick={() => navigate(`/user-management/${location._id}/location`)}
                      >
                        View location history
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
        {!mapLocations.length && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No locations found in ${formatWindowLabel(locationWindowHours)}`}
            style={{ margin: "16px 0 0" }}
          />
        )}
      </Card>

      <Card title="Users and latest location" style={{ marginTop: 16 }}>
        <Table
          rowKey={(record) => String(record._id)}
          dataSource={filteredLocations}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
          columns={[
            { title: "#", width: 55, render: (_, __, index) => index + 1 },
            { title: "Name", dataIndex: "name", render: (value) => value || "—" },
            { title: "Mobile", dataIndex: "mobileNumber", render: (value) => value || "—" },
            { title: "Latest location", dataIndex: "lastLocation", render: (value) => value || "—" },
            { title: "Last seen", dataIndex: "lastLocationAt", render: formatLastSeen },
            {
              title: "Action",
              width: 150,
              render: (_, record) => (
                <Button
                  type="link"
                  disabled={!hasValidCoordinates(record)}
                  onClick={() => navigate(`/user-management/${record._id}/location`)}
                >
                  View history
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={inactiveUsersVisible}
        title={`Inactive Users (${formatWindowLabel(locationWindowHours, true)})`}
        onCancel={() => setInactiveUsersVisible(false)}
        footer={[
          <Button key="close" onClick={() => setInactiveUsersVisible(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={downloadInactiveUsersExcel}
          >
            Download Excel
          </Button>,
        ]}
        width={560}
      >
        <Table
          dataSource={inactiveUserList}
          rowKey={(record) => String(record._id)}
          pagination={{ pageSize: 8, showSizeChanger: false }}
          columns={[
            {
              title: "#",
              key: "index",
              width: 60,
              render: (_, __, index) => index + 1,
            },
            {
              title: "Name",
              dataIndex: "name",
              key: "name",
              render: (value) => value || "—",
            },
            {
              title: "Mobile Number",
              dataIndex: "mobileNumber",
              key: "mobileNumber",
              render: (value) => value || "—",
            },
          ]}
          size="small"
        />
      </Modal>
    </div>
  );
}
